<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, watch } from 'vue';
import api from '@/services/api.js';
import Swal from 'sweetalert2';

// Modelos bidireccionales (Vue 3.4+)
const regional = defineModel('regional');
const gerencia = defineModel('gerencia');
const seccion = defineModel('seccion');
const cargo = defineModel('cargo');
const usuario = defineModel('usuario');
const usuarioObj = defineModel('usuarioObj');

// Props para personalización de estilos
const props = defineProps({
  seccionBorderClass: { type: String, default: 'border-info-light' },
  usuarioBorderClass: { type: String, default: 'border-primary-light' },
  colClass: { type: String, default: 'col-12 col-md-6' },
  colClassUsuario: { type: String, default: 'col-12' },
  labelClass: { type: String, default: 'label-custom' },
  selectClass: { type: String, default: 'form-select-tamep select-custom' }
});

const listas = reactive({
  regionales: [],
  gerencias: [],
  secciones: [],
  cargos: [],
  usuarios: []
});

const listaPersonalMaster = ref([]);
const cargando = ref(false);

onMounted(async () => {
  try {
    cargando.value = true;
    const { data } = await api.get('designaciones/estructura_completa/');
    listaPersonalMaster.value = data;
    
    // Extraer regionales únicas de la lista maestra
    const unicas = [];
    const map = new Map();
    for (const item of data) {
      if (!map.has(item.regional_id)) {
        map.set(item.regional_id, true);
        unicas.push({ id: item.regional_id, nombre: item.regional_nombre });
      }
    }
    listas.regionales = unicas;
  } catch (e) {
    console.error("Error cargando estructura de personal:", e);
    Swal.fire("Error", "No se pudo obtener la lista de personal.", "error");
  } finally {
    cargando.value = false;
  }
});

// 1. Regional -> Gerencias
watch(regional, (newVal) => {
  if (newVal) {
    const filtrados = listaPersonalMaster.value.filter(p => p.regional_id === newVal);
    listas.gerencias = [...new Map(filtrados.map(item => [item.gerencia_id, { id: item.gerencia_id, nombre: item.gerencia_nombre }])).values()];

    if (listas.gerencias.length === 0) {
      Swal.fire('Regional sin personal', 'No hay funcionarios registrados en esta Regional actualmente.', 'warning');
    }
  } else {
    listas.gerencias = [];
  }
  // Resetear hijos
  gerencia.value = '';
  seccion.value = '';
  cargo.value = '';
  usuario.value = '';
});

// 2. Gerencia -> Secciones
watch(gerencia, (newVal) => {
  if (newVal) {
    const filtrados = listaPersonalMaster.value.filter(p => p.gerencia_id === newVal);
    listas.secciones = [...new Map(
      filtrados
        .filter(item => item.seccion_id)
        .map(item => [item.seccion_id, { id: item.seccion_id, nombre: item.seccion_nombre }])
    ).values()];

    if (listas.secciones.length === 0) {
      Swal.fire({
        title: 'Gerencia Vacía',
        text: 'No hay secciones o personal registrado bajo esta Gerencia. Verifique el módulo de Usuarios.',
        icon: 'info',
        confirmButtonColor: '#003366'
      });
    }
  } else {
    listas.secciones = [];
  }
  // Resetear hijos
  seccion.value = '';
  cargo.value = '';
  usuario.value = '';
});

// 3. Sección -> Cargos
watch(seccion, (newVal) => {
  if (newVal) {
    const filtrados = listaPersonalMaster.value.filter(p => p.seccion_id === newVal);
    listas.cargos = [...new Map(
      filtrados
        .filter(item => item.cargo_id)
        .map(item => [item.cargo_id, { id: item.cargo_id, nombre: item.cargo_nombre }])
    ).values()];

    if (listas.cargos.length === 0) {
      Swal.fire({
        title: 'Sección Vacía',
        text: 'No hay cargos operativos registrados en esta Sección.',
        icon: 'info',
        confirmButtonColor: '#003366'
      });
    }
  } else {
    listas.cargos = [];
  }
  // Resetear hijos
  cargo.value = '';
  usuario.value = '';
});

// 4. Cargo -> Usuarios
watch(cargo, (newVal) => {
  if (newVal) {
    listas.usuarios = listaPersonalMaster.value.filter(p => p.cargo_id === newVal && p.seccion_id === seccion.value);

    if (listas.usuarios.length === 0) {
      Swal.fire({
        title: 'Cargo Vacante',
        text: 'Aún no hay ningún funcionario ocupando este cargo.',
        icon: 'info',
        toast: true,
        position: 'top-end',
        timer: 4000,
        showConfirmButton: false
      });
    }
  } else {
    listas.usuarios = [];
  }
  usuario.value = '';
});

// 5. Usuario seleccionado -> usuarioObj
watch(usuario, (newVal) => {
  if (newVal) {
    const u = listas.usuarios.find(item => item.id === newVal);
    usuarioObj.value = u || null;
  } else {
    usuarioObj.value = null;
  }
});
</script>

<template>
  <!-- Regional -->
  <div :class="colClass">
    <label :class="labelClass">Regional</label>
    <select v-model="regional" :class="selectClass" :disabled="cargando">
      <option value="">Seleccione...</option>
      <option v-for="r in listas.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
    </select>
  </div>

  <!-- Gerencia -->
  <div :class="colClass">
    <label :class="labelClass">Gerencia</label>
    <select v-model="gerencia" :disabled="!regional || cargando" :class="selectClass">
      <option value="">Seleccione...</option>
      <option v-for="g in listas.gerencias" :key="g.id" :value="g.id">{{ g.nombre }}</option>
    </select>
  </div>

  <!-- Sección -->
  <div :class="colClass">
    <label :class="[labelClass, 'text-info'].join(' ')">Sección / Área</label>
    <select v-model="seccion" :disabled="!gerencia || cargando" :class="[selectClass, seccionBorderClass].join(' ')">
      <option value="">Seleccione...</option>
      <option v-for="s in listas.secciones" :key="s.id" :value="s.id">{{ s.nombre }}</option>
    </select>
  </div>

  <!-- Cargo -->
  <div :class="colClass">
    <label :class="labelClass">Cargo</label>
    <select v-model="cargo" :disabled="!seccion || cargando" :class="selectClass">
      <option value="">Seleccione...</option>
      <option v-for="c in listas.cargos" :key="c.id" :value="c.id">{{ c.nombre }}</option>
    </select>
  </div>

  <!-- Funcionario Receptor / Responsable -->
  <div :class="colClassUsuario">
    <label :class="labelClass">Funcionario Responsable</label>
    <select v-model="usuario" :disabled="!cargo || cargando" :class="[selectClass, usuarioBorderClass].join(' ')">
      <option value="">Seleccione al funcionario...</option>
      <option v-for="u in listas.usuarios" :key="u.id" :value="u.id">{{ u.nombre_completo }}</option>
    </select>
  </div>
</template>
